import React, { useState } from "react";
import Administration from "../../components/Administration";
import axios from "axios";
import useErrorHandler from "../../hooks/userErrorHandler";
import useCompanyTree from "../../hooks/useCompanyTree";
import useSearchAdministration from "../../hooks/useSearchAdministration";
import useUser from "../../hooks/useUser";
import useBranch from "../../hooks/useBranch";
import useCompany from "../../hooks/useCompany/useCompany";
import PageContainer from "../PageContainer";
import usePermissions from "../../hooks/usePermissions";
import useCurrencies from "../../hooks/useCurrencies";

export default () => {
  const permissions = usePermissions();
  const [duplicateEmail, setDuplicateEmail] = useState<boolean>(false);
  const [companyChangeCount, setCompanyChangeCount] = useState<number>(0);
  const [companyUpdateCount, setCompanyUpdateCount] = useState<number>(0);
  const [branchChangeCount, setBranchChangeCount] = useState<number>(0);
  const [userChangeCount, setUserChangeCount] = useState<number>(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>();
  const [selectedBranchId, setSelectedBranchId] = useState<number>();
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [searchText, setSearchText] = useState<string>("");
  const setError = useErrorHandler();
  const treeData = useCompanyTree(
    selectedCompanyId,
    selectedBranchId,
    companyChangeCount,
    branchChangeCount,
    userChangeCount,
    permissions
  );
  const searchResults = useSearchAdministration(searchText);
  const { company, companyLoading } = useCompany(
    selectedCompanyId,
    permissions,
    branchChangeCount,
    companyUpdateCount
  );
  const { branch, branchLoading } = useBranch(
    selectedCompanyId,
    selectedBranchId,
    permissions,
    userChangeCount,
    branchChangeCount
  );
  const { user } = useUser(selectedCompanyId, selectedUserId, [
    userChangeCount
  ]);
  const { currencies, currenciesLoading } = useCurrencies();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  async function handleUpdateCompany(companyDetails: any) {
    try {
      setIsSaving(true);
      await axios.put(`/api/Companies/${selectedCompanyId}`, {
        name: companyDetails.name,
        fxRateType: parseInt(companyDetails.fxRateType),
        rateCardEmailAddresses: companyDetails.rateCardEmailAddresses,
        orderCharge: parseFloat(companyDetails.orderCharge),
        returnCharge: parseFloat(companyDetails.returnCharge),
        marginsAndCharges: companyDetails.marginsAndCharges,
        fcaCurrencies:
          companyDetails.fcaCurrencies.length > 0
            ? companyDetails.fcaCurrencies.map((x: any) => x.id)
            : [],
        settlement: parseInt(companyDetails.settlement),
        version: company.version
      });
      setCompanyUpdateCount(c => c + 1);
      setCompanyChangeCount(c => c + 1);
    } catch (err) {
      if (
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey ===
          "Company.UpdatedByAnotherUser"
      ) {
        setError(
          "Company was updated by another user. Refreshing tree.",
          err,
          () => {
            setCompanyUpdateCount(c => c + 1);
            setCompanyChangeCount(c => c + 1);
          }
        );
      } else {
        setError("Unable to update company. Please try again.", err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateBranch(branchDetails: any) {
    try {
      setIsSaving(true);
      await axios.put(
        `/api/Companies/${selectedCompanyId}/Branches/${selectedBranchId}`,
        {
          name: branchDetails.name,
          contactName: branchDetails.contactName,
          streetAddress: branchDetails.streetAddress,
          city: branchDetails.city,
          county: branchDetails.county,
          postCode: branchDetails.postCode,
          intrafinId: branchDetails.intrafinId,
          deliveryMethod: parseInt(branchDetails.deliveryMethod),
          sellingDealType: parseInt(branchDetails.sellingDealType),
          buyingDealType: parseInt(branchDetails.buyingDealType),
          valueDateModifier: parseInt(branchDetails.valueDateModifier),
          despatchDateModifier: parseInt(branchDetails.despatchDateModifier),
          returnValueDateModifier: parseInt(
            branchDetails.returnValueDateModifier
          ),
          returnDespatchDateModifier: parseInt(
            branchDetails.returnDespatchDateModifier
          ),
          version: branch.version
        }
      );
      setCompanyChangeCount(c => c + 1);
      setBranchChangeCount(c => c + 1);
    } catch (err) {
      if (
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey ===
          "Branch.UpdatedByAnotherUser"
      ) {
        setError(
          "Branch was updated by another user. Refreshing tree.",
          err,
          () => {
            setCompanyChangeCount(c => c + 1);
            setBranchChangeCount(c => c + 1);
          }
        );
      } else {
        setError("Unable to update branch. Please try again.", err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateUser(userDetails: any) {
    try {
      setIsSaving(true);
      await axios.put(
        `/api/Companies/${selectedCompanyId}/Users/${selectedUserId}`,
        {
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails.email,
          branchIds: userDetails.branchIds,
          orderLimit: userDetails.orderLimit,
          dailyLimit: userDetails.dailyLimit,
          version: user.version
        }
      );
      setUserChangeCount(c => c + 1);
      setDuplicateEmail(false);
      setBranchChangeCount(c => c + 1);
    } catch (err) {
      if (
        err.response.data.problems &&
        err.response.data.problems[0].problemKey === "User.Email.NotUnique"
      ) {
        setDuplicateEmail(true);
      } else if (
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey === "User.UpdatedByAnotherUser"
      ) {
        setError(
          "User was updated by another user. Refreshing tree.",
          err,
          () => {
            setUserChangeCount(c => c + 1);
            setDuplicateEmail(false);
            setBranchChangeCount(c => c + 1);
          }
        );
      } else {
        setError("Unable to update user. Please try again.", err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="CompaniesContainer-container">
      <PageContainer title="Administration">
        <Administration
          permissions={permissions}
          onSearchChange={(t: string) => setSearchText(t)}
          treeData={treeData}
          searchResults={searchResults}
          selectedCompany={company}
          selectedBranch={branch}
          selectedUser={user}
          companyLoading={companyLoading}
          branchLoading={branchLoading}
          onLoadCompany={(companyId: any) => {
            setSelectedBranchId(undefined);
            setSelectedCompanyId(companyId);
            setSelectedUserId(undefined);
          }}
          onLoadBranch={(companyId: any, branchId: any) => {
            setSelectedCompanyId(companyId);
            setSelectedBranchId(branchId);
            setSelectedUserId(undefined);
          }}
          onLoadUser={(companyId: any, userId: any) => {
            setSelectedBranchId(undefined);
            setSelectedCompanyId(companyId);
            setSelectedUserId(userId);
          }}
          onCreateCompany={async (companyDetails: any) => {
            try {
              setIsSaving(true);
              await axios.post("/api/Companies", {
                name: companyDetails.name,
                fxRateType: parseInt(companyDetails.fxRateType),
                rateCardEmailAddresses: companyDetails.rateCardEmailAddresses,
                orderCharge: parseFloat(companyDetails.orderCharge),
                returnCharge: parseFloat(companyDetails.returnCharge),
                marginsAndCharges: companyDetails.marginsAndCharges,
                fcaCurrencies:
                  companyDetails.fcaCurrencies.length > 0
                    ? companyDetails.fcaCurrencies.map((x: any) => x.id)
                    : [],
                settlement: parseInt(companyDetails.settlement)
              });
              setCompanyChangeCount(c => c + 1);
            } catch (err) {
              setError("Unable to create company. Please try again.", err);
            } finally {
              setIsSaving(false);
            }
          }}
          onUpdateCompany={handleUpdateCompany}
          onCreateBranch={async (branchDetails: any) => {
            try {
              setIsSaving(true);
              await axios.post(`/api/Companies/${selectedCompanyId}/Branches`, {
                name: branchDetails.name,
                contactName: branchDetails.contactName,
                streetAddress: branchDetails.streetAddress,
                city: branchDetails.city,
                county: branchDetails.county,
                postCode: branchDetails.postCode,
                intrafinId: branchDetails.intrafinId,
                deliveryMethod: parseInt(branchDetails.deliveryMethod),
                sellingDealType: parseInt(branchDetails.sellingDealType),
                buyingDealType: parseInt(branchDetails.buyingDealType),
                valueDateModifier: parseInt(branchDetails.valueDateModifier),
                despatchDateModifier: parseInt(
                  branchDetails.despatchDateModifier
                ),
                returnValueDateModifier: parseInt(
                  branchDetails.returnValueDateModifier
                ),
                returnDespatchDateModifier: parseInt(
                  branchDetails.returnDespatchDateModifier
                )
              });
              setBranchChangeCount(c => c + 1);
            } catch (err) {
              setError("Unable to create branch. Please try again.", err);
            } finally {
              setIsSaving(false);
            }
          }}
          onUpdateBranch={handleUpdateBranch}
          onCreateUser={async (userDetails: any) => {
            try {
              setIsSaving(true);
              await axios.post(
                `/api/Companies/${selectedCompanyId}/Users`,
                userDetails
              );
              setUserChangeCount(c => c + 1);
              setDuplicateEmail(false);
            } catch (err) {
              if (
                err.response &&
                err.response.data &&
                err.response.data.problems &&
                err.response.data.problems[0].problemKey ===
                  "User.Email.NotUnique"
              ) {
                setDuplicateEmail(true);
              } else {
                setError("Unable to create user. Please try again.", err);
              }
            } finally {
              setIsSaving(false);
            }
          }}
          duplicateEmail={duplicateEmail}
          userChangeCount={userChangeCount}
          onUpdateUser={handleUpdateUser}
          currencies={currencies}
          currenciesLoading={currenciesLoading}
          isSaving={isSaving}
        />
      </PageContainer>
    </div>
  );
};
